import { useConversationMessageMediaContentState } from '@modules/conversation/ConversationMediaContentView/useConversationMessageMediaContent';
import { useConversationEditorConfig } from '@modules/conversation/hooks/useConversationEditorConfig';
import { ConversationMediaContextProvider } from '@modules/conversation/providers/ConversationMedia';
import { type EditorOptions } from '@tiptap/react';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { ConversationMessageContentType } from 'src/@types/conversation';
import { IWorkspaceUser } from 'src/@types/workspace-info';
import { useMediaPreviewModal } from 'src/features/MediaPreview';
import { MediaMessageReturn } from 'src/features/MediaPreview/types';
import { TableColumnCommentType } from 'src/generated/graphql';
import { useConversationMediaUpload } from 'src/hooks/useConversationMediaUpload';
import { useLocales } from 'src/locales';
import { useConversationContext } from 'src/provider/ConversationContext';
import { chatFileType } from '../../../chat';
import { useBoardUsers } from '../../shared/useBoardUsers';
import { useSendActivitiesMessageMutation } from './useSendActivitiesMessageMutation';

interface ITableRowActivitiesContext {
  rowId: string;
  tableId: string;
  boardId: string;
  editorConfig: Partial<EditorOptions>;
  participants: IWorkspaceUser[];
  handleTextMessage: (message: string) => void;
  handleFileSelect: (type: ConversationMessageContentType, file: File) => void;
}

export const Context = createContext<ITableRowActivitiesContext | null>(null);

type Props = {
  rowId: string;
  tableId: string;
  boardId: string;
  children: React.ReactNode;
};

export function TableRowActivitiesContextProvider({
  rowId,
  tableId,
  boardId,
  children,
}: Props) {
  const { activitiesSendMessage } = useSendActivitiesMessageMutation();
  const { replyTo, setReplyTo, setScrollEnd, setShowInput } =
    useConversationContext();
  const { uploadMediaFile } = useConversationMediaUpload();
  const { setMediaContent } = useConversationMessageMediaContentState();
  const { showMediaPreviewModal } = useMediaPreviewModal();
  const { translate } = useLocales();
  const participants = useBoardUsers(boardId);

  const handleScrollEnd = useCallback(() => {
    setScrollEnd(true);
    setTimeout(() => setScrollEnd(false), 200);
  }, [setScrollEnd]);

  const handleSendMessage = useCallback(
    async (message: any) => {
      await activitiesSendMessage(message);
      setReplyTo(null);
    },
    [activitiesSendMessage, setReplyTo],
  );

  const onSendFile = useCallback(
    async (
      type: ConversationMessageContentType,
      file: File,
      content: string,
    ) => {
      const { _id, mimeType, size, name } = await uploadMediaFile(file);
      const message = {
        content,
        mediaRef: _id,
        rowId,
        tableId,
        boardId,
        mimeType,
        fileName: name,
        fileSize: size,
        contentType: type,
      };

      setMediaContent(message.content, {
        mediaContent: URL.createObjectURL(file),
        percentageLoaded: 0,
      });
      await handleSendMessage({
        ...message,
        replyTo: replyTo?._id,
      });

      handleScrollEnd();
    },
    [
      setMediaContent,
      replyTo?._id,
      handleScrollEnd,
      uploadMediaFile,
      rowId,
      tableId,
      boardId,
      handleSendMessage,
    ],
  );

  const handleSendMedia = useCallback(
    async ({ message, file, type }: MediaMessageReturn) => {
      setShowInput(true);
      await onSendFile(type, file, message);
    },
    [onSendFile, setShowInput],
  );

  const handleChatUploadFile = async (file: File) => {
    handleOpenMediaPreviewModal(chatFileType(file.type), file);
  };

  const { editorConfig } = useConversationEditorConfig({
    participants,
    handleChatUploadFile,
    placeholder: translate('shared.editor.comments_placeholder'),
  });

  const handleOpenMediaPreviewModal = useCallback(
    (type: ConversationMessageContentType, file: File) => {
      setShowInput(false);
      showMediaPreviewModal({
        file,
        editorConfig,
        setShowInput,
        conversationId: rowId,
        sendMedia: async (response: Omit<MediaMessageReturn, 'type'>) => {
          await handleSendMedia({ ...response, type });
        },
      });
    },
    [showMediaPreviewModal, editorConfig, handleSendMedia, setShowInput, rowId],
  );

  const processFileSelection = useCallback(
    (type: ConversationMessageContentType, file: File) => {
      if (
        type === ConversationMessageContentType.VIDEO ||
        type === ConversationMessageContentType.IMAGE
      ) {
        return handleOpenMediaPreviewModal(type, file);
      }
      return handleSendMedia({ type, file, message: '' });
    },
    [handleOpenMediaPreviewModal, handleSendMedia],
  );

  const value = useMemo(
    () => ({
      rowId,
      tableId,
      boardId,
      editorConfig,
      participants,
      handleTextMessage: async (content: string) => {
        const message = {
          rowId,
          tableId,
          boardId,
          content,
          contentType: TableColumnCommentType.Text,
          replyTo: replyTo?._id,
        };
        await handleSendMessage(message);
      },
      handleFileSelect: processFileSelection,
    }),
    [
      rowId,
      tableId,
      boardId,
      editorConfig,
      replyTo?._id,
      participants,
      processFileSelection,
      handleSendMessage,
    ],
  );

  return (
    <Context.Provider value={value}>
      <ConversationMediaContextProvider>
        {children}
      </ConversationMediaContextProvider>
    </Context.Provider>
  );
}

export function useTableRowActivitiesContext() {
  const context = useContext(Context);

  if (!context)
    throw new Error(
      'useTableRowActivitiesContext must be use inside TableRowActivitiesContextProvider',
    );

  return context;
}
